import React, { Fragment, useContext, useMemo } from "react";
import { StructuredText, renderRule } from "react-datocms";
import {
    isEmptyDocument,
    isLink,
    isParagraph
} from "datocms-structured-text-utils";

import { generateClassString as gcs } from "../../lib/helper/generateClassString";

import GeneralPageContext from "../../templates/GeneralPageContext";

import Link from "../Link";
import { Button } from "../Button";

import ElementContext from "../ElementContext";
import { isDebug } from "../../lib/helper/variables";
import { formatMessage } from "../../lib/helper/logMessageFormat";
import { ACTIVE_FILTERS_KEY } from "../../lib/hooks/useCameraFilter";
import { slugify } from "../../lib/helper/getAnchorNavData";

import * as styles from "./component.module.less";

export function StructuredTextRenderer({
    data,
    className,
    itemLinkClass,
    itemLinkType,
    itemLinkIcon,
    itemLinkIconPosition,
    itemLinkState = {},
    renderLinkToRecord,
    customRules,
    noContainer,
    plainLink,
    emptyLink,
    children,
    contentElementType = "Unknown",
    urlFragment = ""
}) {
    const { pageId, currentSlug } = useContext(GeneralPageContext);
    const { isLightBg } = useContext(ElementContext);

    if (!(data && "links" in data && "blocks" in data && "value" in data)) {
        console.warn(
            `The StructuredText must contain blocks, links and value! Found on content element: [${contentElementType}] on record [${pageId}].`
        );
        return null;
    }

    if (!data.value) {
        return null;
    }

    if (isEmptyDocument(data)) {
        isDebug &&
            formatMessage(
                pageId,
                `Structured text is empty [${contentElementType}].`
            );
        return null;
    }

    function transformMeta(meta) {
        const metaObject = {};
        const classes = [itemLinkClass ?? ""];
        const activeFilters = new Set([]);

        function addOption(key, valueString) {
            const options = valueString.split(",").map((value) => {
                return { value: value.trim() };
            });

            activeFilters.add({
                key,
                options
            });
        }

        if (meta) {
            meta.forEach((entry) => {
                entry.id = entry.id.toLowerCase();

                switch (entry.id) {
                    case "button":
                        metaObject.button = entry.value;
                        break;
                    case "icon":
                        metaObject.icon = entry.value;
                        break;
                    case "floating":
                        classes.push(`float-${entry.value}`);
                        break;
                    case "class":
                        classes.push(entry.value);
                        break;
                    case "camera_category":
                        addOption("category", entry.value);
                        break;
                    case "application_and_industry":
                        addOption("application", entry.value);
                        break;
                    case "camera_series":
                        addOption("series", entry.value);
                        break;
                    case "camera_image_format":
                        addOption("imageFormat", entry.value);
                        break;
                    case "camera_thermal_resolution":
                        addOption("thermalResolution", entry.value);
                        break;
                    case "fov_camera_id":
                        metaObject.fovCameraId = entry.value;
                        break;
                    case "anker":
                        metaObject.anchor = `${slugify(entry.value)}`;
                        break;
                    default:
                        metaObject[entry.id] = entry.value;
                }
            });
        }

        if (activeFilters.size) {
            metaObject["cameraFilters"] = JSON.stringify(
                Array.from(activeFilters)
            );
        }

        return { metaClasses: gcs(...classes), ...metaObject };
    }

    function DefaultRenderLinktoRecord({ record, children, transformedMeta }) {
        const {
            metaClasses,
            button: buttonType,
            icon,
            cameraFilters,
            anchor,
            fovCameraId,
            ...otherMeta
        } = transformedMeta || {};

        const linkClass = gcs(itemLinkClass, metaClasses, [
            noContainer && className,
            className
        ]);

        record.url = record.url === "/index/" ? "/" : record.url;

        const target =
            record.isFileUrl === true || record.isExternalUrl === true
                ? "_blank"
                : undefined;
        
        const to = `${record.url}${
            fovCameraId ? `?fovCameraId=${fovCameraId}` : ""
        }${
            anchor ?? urlFragment ? `#${anchor ?? urlFragment}` : ""
        }`;

        const eventHandler = {};

        if (cameraFilters != null) {
            eventHandler.onClick = (event) => {
                window.sessionStorage.setItem(
                    ACTIVE_FILTERS_KEY,
                    cameraFilters
                );

                if (record.url === currentSlug) {
                    event.preventDefault();
                    window.location.reload();
                }
            };
        }

        const linkState = {
            ...(record?.linkState ?? {}),
            ...itemLinkState
        };

        const customData = useMemo(() => ({ fileId: record?.file?.id }));
        
        return (
            <>
                {(itemLinkType || buttonType) && !emptyLink ? (
                    <Button
                        className={linkClass}
                        renderAs="link"
                        state={linkState}
                        target={target}
                        to={to}
                        type={buttonType || itemLinkType}
                        icon={itemLinkIcon || icon || null}
                        iconPosition={itemLinkIconPosition || null}
                        customdata={customData}
                        {...eventHandler}
                        {...otherMeta}
                    >
                        {children}
                    </Button>
                ) : (
                    <Link
                        linkClass={gcs(linkClass, [
                            isLightBg,
                            styles.dark,
                            styles.light
                        ])}
                        state={linkState}
                        target={target}
                        to={to}
                        customdata={customData}
                        {...eventHandler}
                        {...otherMeta}
                    >
                        {!emptyLink && children}
                    </Link>
                )}
            </>
        );
    }

    const containerClass = gcs(styles.structuredTextContainer, className);

    const defaultLinkRenderRule = renderRule(
        isLink,
        ({ node, children, key }) => {
            const {
                metaClasses,
                button: buttonType,
                icon,
                cameraFilters,
                anchor,
                fovCameraId,
                ...otherMeta
            } = transformMeta(node.meta);

            const linkClass = gcs(itemLinkClass, metaClasses, [
                noContainer && className,
                className
            ]);

            // const onClick =
            //     cameraFilters != null
            //         ? {
            //               onClick: () => {
            //                   window.sessionStorage.setItem(
            //                       ACTIVE_FILTERS_KEY,
            //                       cameraFilters
            //                   );
            //               }
            //           }
            //         : {};

            const eventHandler = {};

            if (cameraFilters != null) {
                eventHandler.onClick = (event) => {
                    window.sessionStorage.setItem(
                        ACTIVE_FILTERS_KEY,
                        cameraFilters
                    );

                    // if (record.url === currentSlug) {
                    //     event.preventDefault();
                    //     window.location.reload();
                    // }
                };
            }

            const to = `${node.url}${
                fovCameraId ? `?fovCameraId=${fovCameraId}` : ""
            }${
                anchor ?? urlFragment ? `#${anchor ?? urlFragment}` : ""
            }`;
            


            return (
                <Fragment key={key}>
                    {(itemLinkType || buttonType) && !emptyLink ? (
                        <Button
                            to={to}
                            renderAs="link"
                            type={buttonType || itemLinkType}
                            className={linkClass}
                            icon={itemLinkIcon || icon || null}
                            iconPosition={itemLinkIconPosition || null}
                            state={itemLinkState}
                            {...eventHandler}
                            {...otherMeta}
                        >
                            {children}
                        </Button>
                    ) : (
                        <Link
                            to={to}
                            linkClass={gcs(linkClass, [
                                isLightBg,
                                styles.dark,
                                styles.light
                            ])}
                            state={itemLinkState}
                            {...eventHandler}
                            {...otherMeta}
                        >
                            {children}
                        </Link>
                    )}
                </Fragment>
            );
        }
    );

    const noParagraphRenderRule = renderRule(
        isParagraph,
        ({ children, key }) => {
            return <Fragment key={key}>{children}</Fragment>;
        }
    );

    const renderRules = customRules ?? [defaultLinkRenderRule];

    if (plainLink) {
        renderRules.push(noParagraphRenderRule);
    }

    function renderStructuredText() {
        return (
            <StructuredText
                data={data}
                metaTransformer={({ meta }) => transformMeta(meta)}
                renderInlineRecord={() => {
                    console.warn(
                        `The StructuredText does not render blocks! Found on content element: [${contentElementType}] on record [${pageId}].`
                    );
                    return null;
                }}
                renderLinkToRecord={
                    renderLinkToRecord ?? DefaultRenderLinktoRecord
                }
                customNodeRules={renderRules}
            >
                {children}
            </StructuredText>
        );
    }

    return (
        <>
            {noContainer ? (
                renderStructuredText()
            ) : (
                <div className={containerClass}>{renderStructuredText()}</div>
            )}
        </>
    );
}
